import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import UsageTypeDropdown from '../formFields/UsageTypeDropdown';
import BasePlatformNumbersAccordion from './BasePlatformNumbersAccordion';
import CallingProfileDropdown from '../formFields/CallingProfileDropdown';
import {
    Button as ChakraButton,
    ModalBody,
    ModalHeader,
    ModalOverlay,
    ModalContent,
    ModalCloseButton,
    ModalFooter,
    useDisclosure,
} from '@chakra-ui/react';
import TrunkType from '@/utils/enums/TrunkType';
import DisassociateTpmNumberBtn from 'pages/telephony/numbers/edit/accordionComponents/DisassociateTpmNumberBtn';
import { Modal, Text } from 'components/v4';
import { useDeactivate } from 'hooks/useCustomQuery';

const MicrosoftTeamsSection = ({
    errors,
    touched,
    submitData,
    setSubmitData,
    teamsTrunks,
    callingProfiles,
    OC,
    platformID,
    selectedTrunk,
    setSelectedTrunk,
    platform,
    services,
}) => {
    const finalRef = useRef(null);
    const queryClient = useQueryClient();
    const [isDisabled, setIsDisabled] = useState(false);
    const { darkMode } = useSelector((state) => state.settings);
    const trunkList = Array.isArray(teamsTrunks) ? teamsTrunks : [];
    const trunkCount = trunkList.length;
    const isTrunkDisabled = trunkCount <= 0;
    const [isChecked, setIsChecked] = useState(
        !!submitData?.find((platform) => platform?.platformID === platformID)
            ?.trunkID || false,
    );
    const {
        isOpen: isDisassociateTpmOpen,
        onOpen: onDisassociateTpmOpen,
        onClose: onDisassociateTpmClose,
    } = useDisclosure();
    const section = 'teams';
    const isCurrentTrunkOC =
        selectedTrunk?.trunkTypeID === TrunkType.OPERATOR_CONNECT ||
        !submitData?.find((platform) => platform?.platformID === platformID)
            ?.trunkID === undefined;

    const isOCMobile = platform?.operatorConnectUsageID === 'OCMobile';
    const { mutateAsync: deactivateNumbers } = useDeactivate();
    const handleSubmitDn = async () => {
        setIsDisabled(false);
        await deactivateNumbers(
            {
                serviceID: services?.find(
                    (s) => s.name === 'Teams Phone Mobile',
                )?.id,
                phoneNumbers: [platform?.telephoneNumber],
            },
            {
                onSuccess: () => {
                    setIsDisabled(true);
                    queryClient.invalidateQueries({ queryKey: ['numbers'] });
                },
            },
        );
        onDisassociateTpmClose();
    };

    const fields = [
        ...BasePlatformNumbersAccordion({
            errors: errors,
            touched: touched,
            submitData: submitData,
            setSubmitData: setSubmitData,
            platformTrunks: teamsTrunks,
            platformID: platformID,
            section: section,
            selectedTrunk: selectedTrunk,
            trunkCount: trunkCount,
            isTrunkDisabled: isTrunkDisabled,
            setSelectedTrunk: setSelectedTrunk,
            isChecked: isChecked,
            trunkList: trunkList,
            setIsChecked: setIsChecked,
            isCurrentTrunkOC: isCurrentTrunkOC,
        }),
        isChecked && isCurrentTrunkOC && (
            <CallingProfileDropdown
                key={`${section}-callingProfile`}
                setPlatformNumber={setSubmitData}
                errors={errors}
                darkMode={darkMode}
                callingProfiles={callingProfiles}
                submitData={submitData}
                platformID={platformID}
                isCurrentTrunkOC={isCurrentTrunkOC}
            />
        ),
        isChecked && isCurrentTrunkOC && (
            <UsageTypeDropdown
                key={`${section}-usageType`}
                setPlatformNumber={setSubmitData}
                errors={errors}
                darkMode={darkMode}
                OC={OC}
                submitData={submitData}
                platformID={platformID}
                isCurrentTrunkOC={isCurrentTrunkOC}
            />
        ),
        isChecked && isOCMobile && (
            <Fragment key="disassociate-tpm-number">
                <DisassociateTpmNumberBtn
                    onOpen={onDisassociateTpmOpen}
                    darkMode={darkMode}
                    isDisabled={isDisabled}
                />
                <Modal
                    isOpen={isDisassociateTpmOpen}
                    onClose={onDisassociateTpmClose}
                    closeOnOverlayClick={false}
                    isCentered
                    motionPreset="scale"
                    finalFocusRef={finalRef}>
                    <ModalOverlay />
                    <ModalCloseButton />

                    <ModalContent>
                        <ModalHeader>Disassociate Number</ModalHeader>
                        <ModalBody>
                            <Text>
                                Are you sure you would like to disassociate
                                operator number? This cannot be undone.
                            </Text>
                        </ModalBody>
                        <ModalFooter justifyContent="flex-start">
                            <ChakraButton
                                colorScheme="red"
                                onClick={handleSubmitDn}>
                                Disassociate
                            </ChakraButton>
                            <ChakraButton
                                colorScheme="messenger"
                                bg="white"
                                minW="128.4px"
                                marginLeft="30px"
                                variant="outline"
                                onClick={onDisassociateTpmClose}>
                                Close
                            </ChakraButton>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </Fragment>
        ),
    ];

    let filteredFields = fields;

    if (isCurrentTrunkOC) {
        filteredFields = filteredFields.filter(
            (field) => field?.type !== 'CallingProfileDropdown',
            (field) => field?.type !== 'OCConfirmationCheckbox',
            (field) => field?.type !== 'UsageTypeDropdown',
        );
    }
    filteredFields = filteredFields.filter(Boolean);

    return filteredFields;
};

export default MicrosoftTeamsSection;
